<template>
  <div class="container box">
    <div class="ground-transportation-coming-soon">
      <span class="transportation-icon">🚙</span>
      <h1>Ground Transportation Booking Coming Soon!</h1>
      <p class="subtitle">We're working on bringing you convenient ground transportation options to complete your travel experience.</p>

      <div class="features">
        <div class="feature" v-for="feature in features" :key="feature">{{ feature }}</div>
      </div>

      <div class="box has-text-left">
        <h4 class="has-text-weight-bold">Need to book ground transportation today?</h4>
        <p>Contact Gant Travel directly for agent assistance at 877-924-0303 or use the chat button in the lower right corner of your screen.</p>
      </div>

      <form class="email-form" @submit.prevent="handleSubmit">
        <div class="form-group">
          <label for="email">Get notified when it's ready:</label>
          <input type="email" id="email" v-model="formData.email"  @input="validateEmail" :class="{ 'is-danger': emailValidationType === 'is-danger' }" placeholder="your.email@example.com">
          <p class="help" :class="emailValidationType" v-if="emailValidationMessage">{{ emailValidationMessage }}</p>
        </div>

        <div class="form-group">
          <label for="transportationTypes">Which ground transportation services would you most likely book?</label>
        <select id="transportationTypes" v-model="formData.route" @change="validateRoute">
            <option value="">Select your preference</option>
            <option v-for="option in transportationOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        <p class="help" :class="routeValidationType" v-if="routeValidationMessage">{{ routeValidationMessage }}</p>
        </div>

        <button type="submit" class="button is-primary is-fullwidth" :disabled="isSubmitting">
          {{ isSubmitting ? 'Submitting...' : 'Notify Me When Available' }}
        </button>
      </form>

      <div class="success-message" v-show="showSuccessMessage">
        <strong>Thanks!</strong> We'll email you as soon as ground transportation booking is available.
      </div>

      <p class="disclaimer">
        We're currently in development and will launch this feature based on user interest. Your email will only be used to notify you about ground transportation booking availability.
      </p>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"

export default {
  components: {},

  props: {},

  data() {
    return {
      features: [
        'Airport shuttles and private transfers',
        'Shared ride services',
        'Integrated with your flight and hotel bookings',
        'Real-time tracking and schedule updates'
      ],
      transportationOptions: [
        { value: 'airport-shuttle', label: 'Airport shuttles' },
        { value: 'private-transfer', label: 'Private car transfers' },
        { value: 'shared-rides', label: 'Shared ride services' },
        { value: 'all', label: 'All of the above' }
      ],
      formData: {
        email: '',
        route: ''
      },
      isSubmitting: false,
      showSuccessMessage: false,
      emailValidationMessage: null,
      emailValidationType: null,
      routeValidationMessage: null,
      routeValidationType: null,
    }
  },

  computed: {
    ...mapState({
      profile: state => state.travellersAndTrips.profile
    })
  },

  watch: {},

  methods: {
    validateEmail() {
      let isValid = false
      const reg = /^[\w.-]+@[\w.-]+\.[a-zA-Z]{2,}$/
      if (reg.test(this.formData.email)) {
        this.emailValidationMessage = null
        this.emailValidationType = null
        isValid = true
      } else {
        this.emailValidationMessage = 'Please enter valid email.'
        this.emailValidationType = 'is-danger'
        isValid = false
      }
      return isValid
    },
    validateRoute() {
      if (this.formData.route) {
        this.routeValidationMessage = null
        this.routeValidationType = null
        return true
      } else {
        this.routeValidationMessage = 'Please select a route preference.'
        this.routeValidationType = 'is-danger'
        return false
      }
    },
    async handleSubmit() {
      const isEmailValid = this.validateEmail()
      const isRouteValid = this.validateRoute()
      if (!isEmailValid || !isRouteValid) return
      this.isSubmitting = true;

      try {
        const result = await this.$app.huddleLabsServerCall('rail/interest/capture', {
          interested_in: 'Ground transportation',
          user_id: this.profile.id,
          user_email: this.profile.email || '',
          notify_email: this.formData.email,
          route: this.formData.route
        });

        if (result.error) {
          throw new Error(result.message || 'Failed to capture ground transportation interest');
        }

        // Show success message
        this.showSuccessMessage = true;

        this.formData = {
          email: this.profile && this.profile.email ? this.profile.email : '',
          route: ''
        };
        this.emailValidationMessage = null
        this.emailValidationType = null

        // Hide success message after 5 seconds
        setTimeout(() => {
          this.showSuccessMessage = false;
        }, 5000);

      } catch (error) {
        console.error('Error submitting form:', error);
      } finally {
        this.isSubmitting = false;
      }
    }
  },

  mounted() {
    // Prefill email if user is logged in
    if (this.profile && this.profile.email) {
      this.formData.email = this.profile.email;
    }

    // Log the event
    this.$app.logEvent({
      event_type: 'Search Started',
      event_properties: {
        item_type: 'ground_transportation'
      }
    });
  },
}
</script>

<style lang="scss">
.ground-transportation-coming-soon {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;

  .transportation-icon {
    font-size: 4rem;
    display: block;
    margin-bottom: 1.5rem;
  }

  h1 {
    font-size: 1.75rem;
    margin-bottom: 1rem;
    color: #2c3e50;
    font-weight: 600;
  }

  .subtitle {
    font-size: 0.9rem;
    color: #7f8c8d;
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }

  .features {
    margin-bottom: 1.5rem;
    text-align: left;

    .feature {
      padding: 0.5rem 0;
      font-weight: 500;
      color: #2c3e50;
      display: flex;
      align-items: center;
      font-size: 0.9rem;

      &:before {
        content: "✓";
        color: #27ae60;
        font-weight: bold;
        margin-right: 0.8rem;
        font-size: 1rem;
      }
    }
  }

  .email-form {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 2rem;

    .form-group {
      margin-bottom: 1.5rem;
      text-align: left;

      label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 600;
        color: #2c3e50;
        font-size: 0.9rem;
      }

      input, select {
        width: 100%;
        padding: 0.8rem;
        border: 2px solid #e1e5e9;
        border-radius: 4px;
        font-size: 0.9rem;
        transition: border-color 0.3s ease;

        &:focus {
          outline: none;
          border-color: #e67e22;
        }
      }

      select {
        background: white;
        cursor: pointer;
      }
    }
  }

  .success-message {
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
    border-radius: 4px;
    padding: 1rem;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }

  .disclaimer {
    color: #7f8c8d;
    font-size: 0.8rem;
    line-height: 1.5;
    font-style: italic;
  }
}

// Mobile responsiveness
@media screen and (max-width: 768px) {
  .ground-transportation-coming-soon {
    padding: 1rem;

    h1 {
      font-size: 1.3rem;
    }

    .transportation-icon {
      font-size: 3rem;
    }

    .email-form {
      padding: 1.5rem;
    }

    .features .feature {
      padding: 0.4rem 0;
    }
  }
}
</style>